import {
  DESKTOP_ASPECT_RATIO,
  MOBILE_ASPECT_RATIO,
  TABLET_ASPECT_RATIO,
} from "../../constants";
import { components } from "@libertify/api-types/media";

export type AspectRatioEnum = components["schemas"]["AspectRatio"];

const ASPECT_RATIO_ENUM_TO_NUMBER: Record<AspectRatioEnum, number> = {
  desktop: DESKTOP_ASPECT_RATIO,
  tablet: TABLET_ASPECT_RATIO,
  mobile: MOBILE_ASPECT_RATIO,
  square: 1,
  classic: 4 / 3,
};

export const aspectRatioEnumToNumber = (value: AspectRatioEnum): number =>
  ASPECT_RATIO_ENUM_TO_NUMBER[value];

export const aspectRatioNumberToEnum = (value: number): AspectRatioEnum => {
  const match = (
    Object.entries(ASPECT_RATIO_ENUM_TO_NUMBER) as [AspectRatioEnum, number][]
  ).find(([, n]) => Math.abs(n - value) < 0.01);
  return match?.[0] ?? "mobile";
};

export const ITEM_ID_TO_INPUT_KEY = {
  main_image: "image",
  title_text: "title",
  subtitle_text: "subtitle",
  quote_text: "quote",
  text: "text",
  figure_0: "figure",
  unit_percent: "unit",
  short_description_text: "short_description",
  paragraph_1_text: "paragraph_1",
  paragraph_2_text: "paragraph_2",
};

export const DEFAULT_IMAGE_INPUT: components["schemas"]["SlideInput"] = {
  input_type: "String",
  type: "Slide",
  attribute: "image",
  description: "The image element to display",
};

export const DEFAULT_TITLE_INPUT: components["schemas"]["SlideInput"] = {
  input_type: "String",
  type: "Slide",
  attribute: "title",
  description: "The title of the slide",
};

export const DEFAULT_SUBTITLE_INPUT: components["schemas"]["SlideInput"] = {
  input_type: "String",
  type: "Slide",
  attribute: "subtitle",
  description: "The subtitle of the slide",
};

export const DEFAULT_TEXT_INPUT: components["schemas"]["SlideInput"] = {
  input_type: "String",
  type: "Slide",
  attribute: "text",
  description: "The text of the slide",
};

export const DEFAULT_QUOTE_INPUT: components["schemas"]["SlideInput"] = {
  input_type: "String",
  type: "Slide",
  attribute: "quote",
  description: "The quote to display.",
};

export const DEFAULT_FIGURE_INPUT: components["schemas"]["SlideInput"] = {
  input_type: "Number",
  type: "Slide",
  attribute: "figure",
  description: "The figure data object",
};

export const DEFAULT_UNIT_INPUT: components["schemas"]["SlideInput"] = {
  input_type: "String",
  type: "Slide",
  attribute: "unit",
  description: "The unit of the figure",
};

export const DEFAULT_SHORT_DESCRIPTION_INPUT: components["schemas"]["SlideInput"] =
  {
    input_type: "String",
    type: "Slide",
    attribute: "short_description",
    description: "The short description of the figure",
  };

export const DEFAULT_PARAGRAPH_1_INPUT: components["schemas"]["SlideInput"] = {
  input_type: "String",
  type: "Slide",
  attribute: "paragraph_1",
  description: "The first paragraph to display",
};

export const DEFAULT_PARAGRAPH_2_INPUT: components["schemas"]["SlideInput"] = {
  input_type: "String",
  type: "Slide",
  attribute: "paragraph_2",
  description: "The second paragraph to display",
};

export const ITEM_ID_TO_DEFAULT_INPUT: Record<
  string,
  components["schemas"]["SlideInput"]
> = {
  main_image: DEFAULT_IMAGE_INPUT,
  title_text: DEFAULT_TITLE_INPUT,
  subtitle_text: DEFAULT_SUBTITLE_INPUT,
  quote_text: DEFAULT_QUOTE_INPUT,
  text: DEFAULT_TEXT_INPUT,
  figure_0: DEFAULT_FIGURE_INPUT,
  unit_percent: DEFAULT_UNIT_INPUT,
  short_description_text: DEFAULT_SHORT_DESCRIPTION_INPUT,
  paragraph_1_text: DEFAULT_PARAGRAPH_1_INPUT,
  paragraph_2_text: DEFAULT_PARAGRAPH_2_INPUT,
};
